import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useMemo, useState } from "react";

/**
 * Bulk operation concurrency tracking state.
 */
export interface BulkOperationState {
	/** Maximum concurrent operations allowed */
	limit: number;
	/** Threshold ratio for higher-risk operations */
	warningThreshold: number;
	/** Active bulk operation IDs */
	activeIds: string[];
	/** Active bulk operation count */
	activeCount: number;
	/** Whether the concurrency limit has been reached */
	isLimitReached: boolean;
	/** Whether the warning threshold has been reached */
	isAboveWarningThreshold: boolean;
	/** Register active workflow run IDs for a workflow source */
	registerActiveRuns: (source: string, runIds: string[]) => void;
}

const BulkOperationContext = createContext<BulkOperationState | null>(null);

export interface BulkOperationProviderProps {
	children: ReactNode;
	/** Maximum concurrent bulk operations */
	limit?: number;
	/** Warning threshold ratio (0-1) */
	warningThreshold?: number;
}

/**
 * Provider for tracking concurrent bulk operations across the UI.
 *
 * @param props - Provider props
 */
export function BulkOperationProvider({
	children,
	limit = 5,
	warningThreshold = 0.5,
}: BulkOperationProviderProps) {
	const [runsBySource, setRunsBySource] = useState<Record<string, string[]>>({});

	const registerActiveRuns = useCallback((source: string, runIds: string[]) => {
		setRunsBySource((prev) => ({
			...prev,
			[source]: runIds,
		}));
	}, []);

	const activeIds = useMemo(() => {
		const allIds = Object.values(runsBySource).flat();
		return Array.from(new Set(allIds));
	}, [runsBySource]);

	const activeCount = activeIds.length;
	const isLimitReached = activeCount >= limit;
	const isAboveWarningThreshold = activeCount >= Math.ceil(limit * warningThreshold);

	const value = useMemo<BulkOperationState>(
		() => ({
			limit,
			warningThreshold,
			activeIds,
			activeCount,
			isLimitReached,
			isAboveWarningThreshold,
			registerActiveRuns,
		}),
		[
			limit,
			warningThreshold,
			activeIds,
			activeCount,
			isLimitReached,
			isAboveWarningThreshold,
			registerActiveRuns,
		],
	);

	return <BulkOperationContext.Provider value={value}>{children}</BulkOperationContext.Provider>;
}

/**
 * Access bulk operation concurrency state.
 */
export function useBulkOperationContext(): BulkOperationState {
	const context = useContext(BulkOperationContext);
	if (!context) {
		throw new Error("useBulkOperationContext must be used within a BulkOperationProvider");
	}
	return context;
}

/**
 * Optional accessor for bulk operation concurrency state.
 */
export function useOptionalBulkOperationContext(): BulkOperationState | null {
	return useContext(BulkOperationContext);
}
